<h2 mat-dialog-title>Add / Edit Price Structure</h2>

<div mat-dialog-content class="flex flex-col justify-start items-stretch gap-y-4 max-w-2xl lg:max-w-3xl">

  @if (hasCustomerPrice$ | async) {
  <p>Modify the pricing structure for this hire. By default it will use the customer pricing structure for this hire.
  </p>
  } @else {
  <p>Modify the pricing structure for this hire. By default it will use the standard pricing structure for this hire.
  </p>
  }

  @if (hasHireSpecificPrice$ | async) {
  <p>This hire has the following special pricing structure, which will override the standard and any customer pricing
    structure for this
    hire.</p>
  }
  @if (defaultLoading$ | async) {
  <div class="flex items-center gap-2 text-sm text-gray-500 mb-2">
    <mat-progress-spinner diameter="20" mode="indeterminate"></mat-progress-spinner>
    Loading standard prices...
  </div>
  }
  @if (default$ | async; as defaultPrices) {
  <p>The standard prices for this product are:</p>
  <div class="flex md:flex-row flex-col justify-start items-start md:justify-between md:items-center md:gap-x-4">
    @for (field of priceFields; track field.name) {
    <app-key-value-pair [name]="field.label"
      [value]="defaultPrices[field.name] | hlCurrency: data.currency | async"></app-key-value-pair>
    }
  </div>
  }

  @if (hireSpecificLoading$ | async) {
  <div class="flex items-center gap-2 text-sm text-gray-500 mb-2">
    <mat-progress-spinner diameter="20" mode="indeterminate"></mat-progress-spinner>
    Loading special prices...
  </div>
  }
  @if(hasCustomerPrice$ | async) {
  @if (customer$ | async; as customerPrices) {
  <p>This customer has the following prices for this product:</p>
  <div class="flex md:flex-row flex-col justify-start items-start md:justify-between md:items-center md:gap-x-4">
    @for (field of priceFields; track field.name) {
    <app-key-value-pair [name]="field.label"
      [value]="customerPrices[field.name] | hlCurrency: data.currency | async"></app-key-value-pair>
    }
  </div>
  }
  }

  @if (customerLoading$ | async) {
  <div class="flex items-center gap-2 text-sm text-gray-500 mb-2">
    <mat-progress-spinner diameter="20" mode="indeterminate"></mat-progress-spinner>
    Loading customer prices...
  </div>
  }
  <!-- Pricing Type Selection -->
  <div class="my-6" [formGroup]="form">
    <h3 class="text-lg font-semibold mb-2">Choose Pricing Type</h3>
    <mat-radio-group formControlName="pricingType" class="grid grid-cols-1 md:grid-cols-2 gap-4" name="pricingType"
      [testId]="testIds.PRICING_TYPE_RADIO_GROUP">

      <div
        class="flex flex-col items-start p-4 border rounded-lg transition-all hover:shadow-md focus-within:ring-2 focus-within:ring-yellow-400 bg-white w-full"
        [ngClass]="{ 'ring-2 ring-yellow-400': form.get('pricingType').value === 'default' }">
        <mat-radio-button value="default" class="w-full" [testId]="testIds.DEFAULT_PRICING_RADIO">
          <span class="flex items-center gap-x-2 mb-1">
            <mat-icon class="text-yellow-500">star</mat-icon>
            <span class="font-medium">Default Pricing</span>
          </span>
          @if(hasCustomerPrice$ | async) {
          <span class="text-xs text-gray-500 text-center">Use the customer pricing structure for this hire.</span>
          } @else {
          <span class="text-xs text-gray-500 text-center">Use the default pricing structure for this hire.</span>
          }
        </mat-radio-button>
      </div>
      <div
        class="flex flex-col items-start p-4 border rounded-lg transition-all hover:shadow-md focus-within:ring-2 focus-within:ring-blue-400 bg-white w-full"
        [ngClass]="{ 'ring-2 ring-blue-400': form.get('pricingType').value === 'fixed' }">
        <mat-radio-button value="fixed" class="w-full" [testId]="testIds.FIXED_PRICING_RADIO">
          <span class="flex items-center gap-x-2 mb-1">
            <mat-icon class="text-blue-500">lock</mat-icon>
            <span class="font-medium">Fixed Price</span>
          </span>
          <span class="text-xs text-gray-500 text-center">Set a single fixed price for this hire regardless of the
            length of the hire.</span>
        </mat-radio-button>
      </div>
      <div
        class="flex flex-col items-start p-4 border rounded-lg transition-all hover:shadow-md focus-within:ring-2 focus-within:ring-green-400 bg-white w-full"
        [ngClass]="{ 'ring-2 ring-green-400': form.get('pricingType').value === 'custom' }">
        <mat-radio-button value="custom" class="w-full" [testId]="testIds.CUSTOM_PRICING_RADIO">
          <span class="flex items-center gap-x-2 mb-1">
            <mat-icon class="text-green-500">tune</mat-icon>
            <span class="font-medium">Special Pricing</span>
          </span>
          <span class="text-xs text-gray-500 text-center">Define a custom pricing structure for this hire.</span>
        </mat-radio-button>
      </div>
      <div
        class="flex flex-col items-start p-4 border rounded-lg transition-all hover:shadow-md focus-within:ring-2 focus-within:ring-red-400 bg-white w-full"
        [ngClass]="{ 'ring-2 ring-red-400': form.get('pricingType').value === 'no_charge' }">
        <mat-radio-button value="no_charge" class="w-full" [testId]="testIds.NO_CHARGE_RADIO">
          <span class="flex items-center gap-x-2 mb-1">
            <mat-icon class="text-red-500">money_off</mat-icon>
            <span class="font-medium">No Charge</span>
          </span>
          <span class="text-xs text-gray-500 text-center">Mark this hire as free of charge. No charge will be
            calculated for this hire.</span>
        </mat-radio-button>
      </div>
    </mat-radio-group>
  </div>


  @switch (form.controls.pricingType.value) {
  @case ('fixed') {
  <div [formGroup]="fixedForm" class="mb-6">
    <h4 class="text-base font-medium mb-2">Fixed Price</h4>
    <mat-form-field class="w-full">
      <mat-label>Fixed Price</mat-label>
      <input matInput type="number" formControlName="fixedPrice" name="fixedPrice" placeholder="Enter fixed price"
        [testId]="testIds.FIXED_PRICE_INPUT" />
      @if (fixedForm.get('fixedPrice').hasError('min') && fixedForm.get('fixedPrice').touched) {
      <mat-error>Fixed price must be at least 0.01</mat-error>
      }
    </mat-form-field>
  </div>
  }
  @case ('custom') {
  <div [formGroup]="customForm" class="mb-6">
    <h4 class="text-base font-medium mb-2">Custom Price Structure</h4>
    <div class="flex md:grid md:grid-cols-2 flex-col gap-2">
      @for (field of priceFields; track field.name) {
      <mat-form-field class="md:w-full">
        <mat-label>{{ field.label }}</mat-label>
        <mat-icon matPrefix [appAddCurrencyPrefix]="customForm.get('currency').value"></mat-icon>
        <input matInput [formControlName]="field.name" [placeholder]="field.label"
          [testId]="testIds.CUSTOM_PRICE_INPUT_PREFIX + field.name" />
      </mat-form-field>
      }
    </div>
    <div class="flex flex-row justify-start items-stretch w-full mt-2">
      <mat-checkbox formControlName="save_to_customer" [testId]="testIds.SAVE_TO_CUSTOMER_CHECKBOX">
        <div class="flex flex-col justify-start items-start w-full">
          <label>Save to customer</label>
          <p class="text-xs text-gray-500">Always use this price when the customer hires this product</p>
        </div>
      </mat-checkbox>
    </div>
  </div>
  }
  @case ('no_charge') {
  <div class="mb-6" [formGroup]="noChargeForm">
    <h4 class="text-base font-medium mb-2">No Charge</h4>
    <mat-checkbox formControlName="free_accessory" [testId]="testIds.FREE_ACCESSORY_CHECKBOX">Free
      Accessory</mat-checkbox>
    @if(!noChargeForm.get('free_accessory').value) {
    <mat-form-field class="w-full mt-2">
      <mat-label>No Charge Reason</mat-label>
      <input matInput formControlName="no_charge_reason" [required]="!noChargeForm.get('free_accessory').value"
        [disabled]="noChargeForm.get('free_accessory').value" [testId]="testIds.NO_CHARGE_REASON_INPUT">
      @if(noChargeForm.get('no_charge_reason').hasError('required')) {
      <mat-error>
        Reason is required unless Free Accessory is checked
      </mat-error>
      }
    </mat-form-field>
    }
  </div>
  }
  }

  @if(itemIsOffHired) {
  <p>This item is off hired, changes to the pricing structure will immediately be reflected in the hire charge.</p>
  }
</div>

<!-- Loading Overlay -->
@if(saving$ | async) {
<div class="fixed inset-0 bg-white bg-opacity-70 flex items-center justify-center z-50"
  [testId]="testIds.LOADING_OVERLAY">
  <mat-progress-spinner mode="indeterminate" diameter="50"></mat-progress-spinner>
</div>
}

<div mat-dialog-actions align="end">
  <button mat-button mat-dialog-close [testId]="testIds.CANCEL_BUTTON">Cancel</button>
  <button mat-button color="primary" [disabled]="pricingType === 'fixed' && !fixedPrice" (click)="save()"
    [testId]="testIds.SAVE_BUTTON">
    Save
  </button>
</div>